import React, { useState, useEffect, useMemo } from 'react';
import { Bell, X, Check, Trash2 } from 'lucide-react';
import { useNotificationStore } from '../store';

// Componente Singola Notifica
function NotificationItem({
  notification,
  onRead,
  onDelete,
}) {
  // Il notification può avere data nested o essere flat
  const data = notification.data || notification;
  const isUnread = !notification.read;

  // Determina colore in base allo score
  const getScoreColor = (score) => {
    if (score >= 90) return 'from-emerald-500/20 to-green-500/20 border-emerald-500/50';
    if (score >= 75) return 'from-amber-500/20 to-orange-500/20 border-amber-500/50';
    return 'from-slate-600/20 to-gray-600/20 border-slate-500/50';
  };

  const scoreStyle = getScoreColor(data?.current_score || 0);

  return (
    <div
      className={`
        p-4 hover:bg-slate-800 transition-all cursor-pointer group
        ${isUnread ? `bg-gradient-to-r ${scoreStyle} border-l-4` : 'bg-slate-900'}
      `}
      onClick={onRead}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-bold text-base text-white">
              {data?.symbol || 'N/A'}
            </h4>
            <span className={`px-2 py-0.5 rounded-md text-xs font-bold uppercase ${
              data?.direction === 'long' 
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' 
                : 'bg-red-500/20 text-red-300 border border-red-500/30'
            }`}>
              {data?.direction || 'LONG'}
            </span>
            {isUnread && (
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
            )}
          </div>

          {/* Message */}
          <p className="text-sm text-slate-300 mb-3 leading-relaxed">
            {data?.message || 'Score aumentato!'}
          </p>

          {/* Score Badge */}
          {data?.current_score && (
            <div className="mb-3 p-2.5 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400 font-medium">SCORE</span>
                <div className="flex items-center gap-2">
                  {data?.previous_score && (
                    <>
                      <span className="text-xs font-mono font-bold text-slate-400">{data.previous_score}%</span>
                      <span className="text-emerald-400 font-bold">→</span>
                    </>
                  )}
                  <span className="text-base font-mono font-bold text-emerald-400">{data.current_score}%</span>
                  {data?.score_increase && (
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs font-bold rounded border border-emerald-500/30">
                      +{data.score_increase}%
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* AOI Info */}
          {data?.aoi_distance_pips !== null && data?.aoi_distance_pips !== undefined && (
            <div className="mb-3 flex items-center gap-2 text-xs text-slate-400">
              <span className="text-cyan-400">📍</span>
              <span className="font-medium">AOI:</span>
              <span className="font-mono font-bold text-cyan-300">{data.aoi_distance_pips} pips</span>
              {data?.aoi_level && (
                <span className="ml-2 font-mono text-slate-500">@ {data.aoi_level.toFixed(5)}</span>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800/50">
            <span className="text-xs text-slate-500 font-mono">
              {notification.timestamp ? new Date(notification.timestamp).toLocaleString('it-IT', { 
                day: '2-digit', 
                month: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit' 
              }) : 'Ora'}
            </span>
            {data?.preset && (
              <span className="text-xs px-2.5 py-1 bg-violet-500/20 text-violet-300 rounded-lg border border-violet-500/30 font-semibold">
                {data.preset.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-1">
          {isUnread && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRead();
              }}
              className="p-1 hover:bg-blue-500/20 rounded transition-colors"
              title="Segna come letta"
            >
              <Check className="w-4 h-4 text-blue-400" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 hover:bg-red-500/20 rounded transition-colors"
            title="Elimina"
          >
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function NotificationCenter({ isMobile = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotificationStore();

  // Se è mobile, mostra sempre aperto (gestito dal parent)
  useEffect(() => {
    if (isMobile) {
      setIsOpen(true);
    }
  }, [isMobile]);

  // Filtra notifiche
  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return notifications;
    return notifications.filter(n => {
      const data = n.data || n;
      return data?.preset === filter;
    });
  }, [notifications, filter]);

  // Preset unici per filtro
  const presets = useMemo(() => {
    const unique = new Set(
      notifications
        .map(n => {
          const data = n.data || n;
          return data?.preset;
        })
        .filter(Boolean) // Rimuove undefined/null
    );
    return Array.from(unique);
  }, [notifications]);

  // Log per debug mobile (solo in sviluppo)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && isMobile) {
      console.log('📱 Mobile NotificationCenter - Notifications:', notifications.length);
      console.log('📱 Mobile NotificationCenter - Unread:', unreadCount);
      console.log('📱 Mobile NotificationCenter - Filtered:', filteredNotifications.length);
    }
  }, [isMobile, notifications, unreadCount, filteredNotifications]);

  // Se è mobile, mostra solo il contenuto senza button e overlay
  if (isMobile) {
    return (
      <div className="h-full w-full flex flex-col overflow-hidden bg-slate-900" style={{ backgroundColor: '#0f172a' }}>
        {/* Header con filtri e azioni - Solo se ci sono notifiche */}
        {notifications.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-800/80 flex items-center justify-between bg-slate-900/95 flex-shrink-0">
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium px-3 py-1.5 rounded-lg hover:bg-blue-500/10 active:scale-95"
                >
                  Segna tutte
                </button>
              )}
            </div>
            <div className="text-xs text-slate-400 font-medium">
              {notifications.length} {notifications.length === 1 ? 'notifica' : 'notifiche'}
            </div>
          </div>
        )}

        {/* Filtri */}
        {presets.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-800/80 flex gap-2 overflow-x-auto scrollbar-hide bg-slate-900/95 flex-shrink-0">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all font-medium flex-shrink-0 active:scale-95 ${
                filter === 'all'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
                  : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              Tutte
            </button>
            {presets.map(preset => (
              <button
                key={preset}
                onClick={() => setFilter(preset)}
                className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all font-medium flex-shrink-0 active:scale-95 ${
                  filter === preset
                    ? 'bg-gradient-to-r from-violet-600 to-purple-500 text-white shadow-lg shadow-violet-500/30'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700/50'
                }`}
              >
                {preset.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        )}

        {/* Lista Notifiche - Scrollabile */}
        <div className="flex-1 overflow-y-auto min-h-0 bg-slate-900">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center mb-4 border border-slate-700/50">
                <Bell className="w-8 h-8 text-slate-500 opacity-50" />
              </div>
              <h3 className="text-base font-semibold text-slate-400 mb-2">
                Nessuna notifica
              </h3>
              <p className="text-sm text-slate-500 max-w-xs leading-relaxed">
                {notifications.length === 0 
                  ? 'Riceverai notifiche quando ci saranno variazioni significative di score per le tue valute monitorate.'
                  : 'Nessuna notifica corrisponde al filtro selezionato.'}
              </p>
              {notifications.length === 0 && (
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg max-w-xs">
                  <p className="text-xs text-blue-400 leading-relaxed">
                    💡 Le notifiche vengono inviate automaticamente quando uno score aumenta significativamente o quando il prezzo si avvicina a un'area di interesse (AOI).
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-800/50">
              {filteredNotifications.map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={() => markAsRead(notification.id)}
                  onDelete={() => deleteNotification(notification.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Badge Icona */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(prev => !prev);
        }}
        className="relative p-2.5 text-slate-400 hover:text-white transition-all group hover:bg-slate-800/60 rounded-lg"
        title="Notifiche"
        type="button"
      >
        <Bell className={`w-5 h-5 transition-all ${isOpen ? 'text-violet-400 scale-110' : 'group-hover:scale-110'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold animate-pulse px-1.5 border-2 border-slate-900">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {unreadCount === 0 && isOpen && (
          <span className="absolute top-0 right-0 bg-violet-500 text-white text-xs rounded-full w-2 h-2 animate-pulse"></span>
        )}
      </button>

      {/* Drawer */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/70 z-[9998]"
            onClick={() => setIsOpen(false)}
          />

          {/* Drawer Content - Posizionato sotto l'header (header è sticky con z-50, questo ha z-[9999]) */}
          <div 
            className="fixed top-[73px] right-4 h-[calc(100vh-73px)] w-[420px] max-w-[calc(100vw-2rem)] shadow-2xl z-[9999] flex flex-col border-l border-slate-800 rounded-tl-lg animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#0f172a', backdropFilter: 'none' }}
          >
            {/* Header */}
            <div className="p-5 border-b border-slate-800 bg-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-500/20 rounded-lg border border-violet-500/30">
                  <Bell className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Centro Notifiche</h2>
                  {unreadCount > 0 && (
                    <p className="text-xs text-slate-400 mt-0.5">
                      {unreadCount} {unreadCount === 1 ? 'non letta' : 'non lette'}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors px-3 py-1.5 rounded-lg hover:bg-blue-500/10 font-medium"
                  >
                    Segna tutte
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  title="Chiudi"
                >
                  <X className="w-5 h-5 text-slate-400 hover:text-white" />
                </button>
              </div>
            </div>

            {/* Filtri */}
            {presets.length > 0 && (
              <div className="p-4 border-b border-slate-800 bg-slate-900 flex gap-2 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all font-medium ${
                    filter === 'all'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700/50'
                  }`}
                >
                  Tutte
                </button>
                {presets.map(preset => (
                  <button
                    key={preset}
                    onClick={() => setFilter(preset)}
                    className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-all font-medium ${
                      filter === preset
                        ? 'bg-gradient-to-r from-violet-600 to-purple-500 text-white shadow-lg shadow-violet-500/30'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50 border border-slate-700/50'
                    }`}
                  >
                    {preset.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            )}

            {/* Lista Notifiche */}
            <div className="flex-1 overflow-y-auto bg-slate-900 min-h-0">
              {filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-slate-800/50 flex items-center justify-center mb-4 border border-slate-700/50">
                    <Bell className="w-10 h-10 text-slate-500 opacity-50" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-400 mb-2">
                    Nessuna notifica
                  </h3>
                  <p className="text-sm text-slate-500 max-w-xs">
                    {notifications.length === 0 
                      ? 'Riceverai notifiche quando ci saranno variazioni significative di score per le tue valute monitorate.'
                      : 'Nessuna notifica corrisponde al filtro selezionato.'}
                  </p>
                  {notifications.length === 0 && (
                    <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <p className="text-xs text-blue-400">
                        💡 Le notifiche vengono inviate automaticamente quando uno score aumenta significativamente o quando il prezzo si avvicina a un'area di interesse (AOI).
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-slate-800/50">
                  {filteredNotifications.map(notification => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onRead={() => markAsRead(notification.id)}
                      onDelete={() => deleteNotification(notification.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

